import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react';
import { Flex } from 'components/Layout/Layout';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

am4core.useTheme(am4themes_animated);

const findHexColor = input => {
  const match = /#(?:[a-f0-9]{3}|[a-f0-9]{6})\b/i.exec(String(input));
  return match ? match[0] : '#000000';
};

function ResultChart({ data }) {
  const chartRef = useRef(null);
  const categoryAxisRef = useRef(null);

  // Avoid DOM id collisions if multiple charts ever render.
  const chartDivId = useMemo(
    () =>
      `chartdiv-${Math.random()
        .toString(36)
        .slice(2)}`,
    []
  );

  useEffect(() => {
    if (!data) return;

    const chart = am4core.create(chartDivId, am4charts.RadarChart);
    chart.hiddenState.properties.opacity = 0;

    const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
    categoryAxis.dataFields.category = 'type';
    categoryAxis.renderer.labels.template.location = 0.5;
    categoryAxis.renderer.labels.template.bent = true;
    categoryAxis.renderer.labels.template.radius = 5;
    categoryAxis.renderer.labels.template.fillOpacity = 1;
    categoryAxis.renderer.labels.template.fontSize = 14;

    categoryAxisRef.current = categoryAxis;

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.min = 0;
    valueAxis.max = 7;
    valueAxis.strictMinMax = true;
    valueAxis.renderer.minGridDistance = 20;
    valueAxis.renderer.grid.template.location = 0;
    valueAxis.renderer.labels.template.fillOpacity = 0;

    const series = chart.series.push(new am4charts.RadarColumnSeries());
    series.dataFields.valueY = 'value';
    series.dataFields.categoryX = 'type';
    series.alignLabels = false;
    series.strokeWidth = 0;
    series.columns.template.tooltipText =
      'Category: {categoryX}\nValue: {valueY}';
    series.columns.template.width = am4core.percent(100);

    series.columns.template.column.adapter.add('fill', (fill, target) => {
      return findHexColor(target?.dataItem?.dataContext?.fill);
    });

    const createTypeRange = type => {
      const axisRange = categoryAxis.axisRanges.create();
      axisRange.axisFill.interactionsEnabled = true;
      axisRange.category = type.type;
      axisRange.endCategory = type.type;
      axisRange.grid.disabled = true;
      axisRange.label.interactionsEnabled = false;
      axisRange.label.bent = true;
      axisRange.label.fontSize = 0;
      axisRange.label.opacity = 0;

      const axisFill = axisRange.axisFill;
      axisFill.innerRadius = am4core.percent(100);
      axisFill.radius = -28;
      axisFill.disabled = false;
      axisFill.fillOpacity = 1;
      axisFill.fill = am4core.color(findHexColor(type.fill));
      axisFill.stroke = '#999';
    };

    const createCategoryRange = (category, typeData) => {
      const axisRange = categoryAxis.axisRanges.create();
      axisRange.axisFill.interactionsEnabled = true;
      axisRange.text = category;
      axisRange.category = typeData[0].type;
      axisRange.endCategory = typeData[typeData.length - 1].type;
      axisRange.grid.disabled = true;
      axisRange.label.interactionsEnabled = false;
      axisRange.label.bent = true;

      const axisFill = axisRange.axisFill;
      axisFill.innerRadius = am4core.percent(115);
      axisFill.radius = -60;
      axisFill.disabled = false;
      axisFill.fillOpacity = 0;
      axisFill.stroke = '#999';

      const axisLabel = axisRange.label;
      axisLabel.location = 0.5;
      axisLabel.fill = am4core.color('#000');
      axisLabel.radius = 40;
      axisLabel.relativeRotation = 0;
      axisLabel.fontSize = 14;
    };

    const generateRadarData = chartData => {
      const catArray = [
        'Autonomie - ondersteuning',
        'Structuur',
        'Controlerend',
        'Chaos'
      ];

      const types = [];
      catArray.forEach(cat => {
        const typeData = chartData[cat] || [];
        types[catArray.indexOf(cat)] = typeData;
        typeData.forEach(type => {
          createTypeRange(type);
        });
        if (typeData.length > 0) {
          createCategoryRange(cat, typeData);
        }
      });

      return types.flat();
    };

    chart.data = generateRadarData(data);

    chartRef.current = chart;

    return () => {
      if (chartRef.current) {
        chartRef.current.dispose();
        chartRef.current = null;
      }
    };
  }, [chartDivId, data]);

  return (
    <Flex flexWrap="wrap" width={1}>
      <div
        id={chartDivId}
        style={{
          width: '100%',
          height: '500px',
          position: 'relative',
          transform: 'scale(0.8)',
          marginTop: -50
        }}
      />
    </Flex>
  );
}

export default observer(ResultChart);
